// src/components/cost-simulator/SizingConfigDrawer.tsx
// Drawer component that replicates the Sizing page configuration controls

import { Button } from '@/components/ui/button';
import { 
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { QuickControlsCollapsible } from '@/components/sizing/QuickControlsCollapsible';
import { Play, X, Loader2 } from 'lucide-react';
import type { UseCostSimulatorSizingReturn } from '@/hooks/useCostSimulatorSizing';

interface SizingConfigDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sizing: UseCostSimulatorSizingReturn;
  onSizingComplete?: () => void;
}

export function SizingConfigDrawer({
  open,
  onOpenChange,
  sizing,
  onSizingComplete,
}: SizingConfigDrawerProps) {
  const handleRunSizing = () => {
    sizing.runSizing();
    onSizingComplete?.();
    onOpenChange(false);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="left">
      <DrawerContent className="h-full w-[400px] max-w-[90vw] rounded-none rounded-r-xl">
        <DrawerHeader className="border-b border-border/50 pb-4">
          <div className="flex items-center justify-between">
            <div>
              <DrawerTitle className="text-lg font-semibold">Configure Sizing</DrawerTitle>
              <DrawerDescription className="text-sm text-muted-foreground">
                Adjust workload parameters to recalculate infrastructure
              </DrawerDescription>
            </div>
            <DrawerClose asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            </DrawerClose>
          </div>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto p-4">
          <QuickControlsCollapsible
            tradeoffs={sizing.scenario.tradeoffs}
            users={sizing.scenario.users}
            totalPct={sizing.totalPct}
            warningUnderPct={sizing.warningUnderPct}
            blockedOverPct={sizing.blockedOverPct}
            catalog={sizing.catalog}
            catalogLoading={sizing.catalogLoading}
            selectedModelId={sizing.scenario.selectedModelId}
            onModelChange={sizing.setSelectedModelId}
            onTradeoffChange={sizing.setTradeoff}
            onUserUpdate={sizing.updateUser}
            onUserRemove={sizing.removeUser}
            onUserAdd={sizing.addUser}
            advanced={sizing.scenario.advanced}
            onAdvancedChange={sizing.setAdvanced}
            presets={sizing.presets}
            onPresetSelect={(presetId) => {
              const preset = sizing.presets.find(p => p.id === presetId);
              if (preset) sizing.applyPreset(preset);
            }}
          />
        </div>

        <DrawerFooter className="border-t border-border/50 pt-4">
          <Button 
            onClick={handleRunSizing} 
            disabled={sizing.loading || sizing.blockedOverPct}
            className="w-full"
            size="lg"
          >
            {sizing.loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" />
                Run Sizing
              </>
            )}
          </Button>
          {sizing.blockedOverPct && (
            <p className="text-xs text-destructive text-center">
              User percentages must total 100% or less
            </p>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
